import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, CalendarCheck, Car, CheckCircle2 } from "lucide-react";
import { to12h } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full px-3 py-2.5 rounded-md bg-[#0A0A0A] border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]";

export default function Rebook() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const mode = params.get("mode") || "rebook";
  const src = params.get("src") || "";
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ requested_vehicle: "", pickup_date: "", return_date: "", package: "", payment_method: "Card (Stripe)", notes: "" });

  useEffect(() => {
    axios.get(`${API}/rebooking/context/${token}${src ? `?src=${src}` : ""}`)
      .then((r) => setCtx(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "This link is invalid or has expired."));
  }, [token, src]);

  const selCar = (ctx?.vehicles || []).find((v) => v.name === f.requested_vehicle);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.requested_vehicle || !f.pickup_date || !f.return_date) { toast.error("Choose a vehicle and your dates."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/rebooking/request`, { driver_token: token, src, ...f });
      setDone(data.message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit your request."); }
    finally { setBusy(false); }
  };

  const scheduleReturn = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/rebooking/schedule-return/${token}`);
      setDone(`Thank you! Your return is confirmed for ${data.return_date} at ${data.return_time || "10:00 AM"}. See you then!`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm."); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-black text-white flex items-center justify-center p-6"><p className="text-white/60">{err}</p></div>;
  if (!ctx) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#F2B705]" /></div>;

  return (
    <div className="min-h-screen bg-black text-white py-10 px-4">
      <div className="max-w-xl mx-auto space-y-5">
        <div>
          <h1 className="font-display font-black text-2xl sm:text-3xl flex items-center gap-2"><RefreshCw className="text-[#F2B705]" /> {mode === "return" ? "Schedule My Return" : "Request Another Rental"}</h1>
          <p className="text-white/50 text-sm mt-1">Hi {ctx.customer.name?.split(" ")[0]} — {mode === "return" ? "confirm your on-time return below." : "pick your next vehicle and dates. Your request goes to AutoHire for approval — it's not confirmed until you receive approval and payment instructions."}</p>
        </div>

        <div className="rounded-lg border border-white/10 bg-[#0C0C0C] p-4 text-sm">
          <div className="flex items-center gap-2 font-semibold"><Car size={15} className="text-[#F2B705]" /> Current rental: {ctx.current.vehicle}</div>
          <div className="text-white/50 mt-1">Due back {ctx.current.return_date} at {to12h(ctx.current.return_time || "10:00 AM")}</div>
          {mode !== "return" && (
            <a data-testid="rebook-keep-vehicle" href={ctx.extend_url} className="inline-block mt-2 text-[#F2B705] text-xs font-bold hover:underline">
              Want to KEEP this vehicle instead? Request an extension →
            </a>
          )}
        </div>

        {done ? (
          <div data-testid="rebook-done" className="rounded-xl border border-emerald-500/40 bg-emerald-500/[0.06] p-6 text-center space-y-2">
            <CheckCircle2 className="mx-auto text-emerald-400" size={36} />
            <p className="text-white/85 text-sm leading-relaxed">{done}</p>
            <Link to="/" className="text-[#F2B705] text-xs font-bold hover:underline">Back to AutoHire</Link>
          </div>
        ) : mode === "return" ? (
          <button data-testid="rebook-schedule-return" onClick={scheduleReturn} disabled={busy}
            className="w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <CalendarCheck size={16} />}
            Confirm — I'll return on {ctx.current.return_date} at {to12h(ctx.current.return_time || "10:00 AM")}
          </button>
        ) : (
          <div className="space-y-4 rounded-xl border border-white/10 bg-[#0C0C0C] p-5">
            <div>
              <label className="text-[11px] text-white/45">Requested vehicle</label>
              <select data-testid="rebook-vehicle" className={`${inp} appearance-none`} value={f.requested_vehicle} onChange={(e) => set("requested_vehicle", e.target.value)}>
                <option value="" className="bg-black">Choose a vehicle…</option>
                {ctx.vehicles.map((v) => (
                  <option key={v.name} value={v.name} className="bg-black">
                    {`${v.name}${v.premium ? ` — PREMIUM (${v.packages.map((p) => `$${p.price} ${p.label}`).join(" / ")})` : v.daily_rate ? ` — $${v.daily_rate}/day` : ""}`}
                  </option>
                ))}
              </select>
              {selCar?.premium && (
                <p className="text-[#F2B705] text-[11px] mt-1.5">Premium vehicle — book exactly {selCar.packages.map((p) => `${p.days} days ($${p.price} ${p.label})`).join(" or ")}. Regular specials don't apply.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[11px] text-white/45">Pickup / continuation date</label>
                <input data-testid="rebook-pickup" type="date" className={`${inp} [color-scheme:dark]`} value={f.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} /></div>
              <div><label className="text-[11px] text-white/45">Return date</label>
                <input data-testid="rebook-return" type="date" className={`${inp} [color-scheme:dark]`} value={f.return_date} onChange={(e) => set("return_date", e.target.value)} /></div>
            </div>
            {selCar?.premium && (
              <div><label className="text-[11px] text-white/45">Preferred package</label>
                <select data-testid="rebook-package" className={`${inp} appearance-none`} value={f.package} onChange={(e) => set("package", e.target.value)}>
                  <option value="" className="bg-black">Match my dates</option>
                  {selCar.packages.map((p) => <option key={p.label} value={`${p.label} — $${p.price}`} className="bg-black">{p.label} — ${p.price} ({p.days} days)</option>)}
                </select></div>
            )}
            <div><label className="text-[11px] text-white/45">Payment method</label>
              <select data-testid="rebook-payment" className={`${inp} appearance-none`} value={f.payment_method} onChange={(e) => set("payment_method", e.target.value)}>
                {ctx.payment_methods.map((m) => <option key={m} value={m} className="bg-black">{m}</option>)}
              </select></div>
            <div><label className="text-[11px] text-white/45">Notes (optional)</label>
              <input data-testid="rebook-notes" className={inp} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></div>
            <p className="text-white/35 text-[11px]">Prefilled for {ctx.customer.name} · {ctx.customer.phone} · {ctx.customer.email}</p>
            <button data-testid="rebook-submit" onClick={submit} disabled={busy}
              className="w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />} Submit Rebooking Request
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
